/**
 * Node Modules
 */
import React, { createElement } from 'react'
import { Helmet } from 'react-helmet'
import marksy from 'marksy/jsx'
import { graphql } from 'gatsby'

/**
 * Application Modules
 */
import PageContainer from '../../components/page-container'
import Metadata from '../../components/metadata'
import Footer from '../../components/footer'
import Header from '../../components/header'

import './style.css'

const compile = marksy({
  createElement,
  elements: {},
})

const privacyPolicy = `
Welcome to Kinzoo. Kinzoo is a private messaging app for families (the “App”) that is operated by Kinzoo Technologies, Inc. (“Kinzoo,” “we,” or “us”). All references to the term "you" or "parent" in this Privacy Policy shall include legal guardians, and all references to "children" or "child" shall mean children under 13 years of age in non- European Union countries, and children under 16 of age in European Union countries, as well as older children for whom you create a child account. This Privacy Policy explains how information is collected, used, and disclosed by Kinzoo, in connection with your or your child’s use of the App or the Kinzoo website. Use of the App involves the collection of personal information from children, and we obtain parental consent to do so in accordance with COPPA and/or the General Data Protection Regulation (“GDPR”). By creating a parent account, you are certifying that you are the parent of that child and authorized to provide such consent.

This Privacy Policy, which complies with the GDPR, applies to all our users.

We may change this Privacy Policy from time to time. If we make changes, we will notify you by revising the date at the top of the Privacy Policy, and in some cases (such as for material changes), we will provide you with additional notice (such as adding a statement to our web site’s homepage or sending you a notification) and obtain your prior verifiable consent (where required).

# **Collection of Information**

Kinzoo is a communication service. To operate the Service, as a contractual requirement between you and Kinzoo, we may collect Personal Information in order to provide our Services. Without such Personal Information, we may not be able to provide our Services.

## Kinzoo App

Information we collect directly from Parents:

- **Account management and profile information:** we collect information from parents when they create a parent account, add another parent/legal guardian or create a child account, request our newsletter, enter a contest, request customer support, leave feedback or otherwise communicate with us. The types of information we may collect include name, display name, email, country, state/province, pin number, profile photo, child’s full name, child’s birth month and year, child’s pin number, and other information parents choose to provide, such as a profile photo for their child.
- **Content:** We collect information from parents when they use the App, such as photographs, audio messages, videos, text messages, and any other information they choose to include in any communications they send.

Information we collect directly from Children:

- **Account management and profile information:** we collect information from children when they update their account and profile, such as pin numbers and profile photos. We also collect feedback which children may submit to us in anonymous form.
- **Content:** We collect information from children when they use the App, such as photographs, audio messages, videos (which are considered personal information), text messages, and any other information they choose to include in any communications they send.

Information we collect automatically:

- **Log and Usage Data:** We may collect log and usage data relating to a user’s use of the App, such as a user’s device information and information about a user’s activities on the App.

## Kinzoo Website

Information we collect directly:

When users visit our website located at kinzoo.com, we may collect information they submit to us directly through the site, such as their email address if they request information about Kinzoo.

Information we collect automatically:

- **Log Information:** We automatically log information about use of the website, including the type of browser used, access times, pages viewed, and IP address.
- **Device Information:** We automatically collect information about the computer or mobile devices used to access the website, including the hardware model, operating system and version, unique device identifiers and mobile network information.
- **Information Collected by Cookies and Other Tracking Technologies:** We use various technologies to collect information, and this may include sending cookies to user computers or mobile devices. For more information, please see our [Cookies Policy](http://kinzoo.com/cookies-policy).

## Other Interactions

For individuals who otherwise interact with us, whether in person, on the website, by phone or email, through social media or otherwise, including individuals who request information about Kinzoo products or services, who sign-up to receive newsletters or other communications, or who enter a contest or respond to surveys and questionnaires, we may collect information they provide to us during these interactions. This information may include name, e-mail address and other contact information.

# **Use of Information**

We use the information we collect from parents and children in the App to provide, maintain, analyze, and improve Kinzoo products and services, including to:

- Facilitate the use of the App. *Applicable legal basis under the GDPR: performance of a contract*;
- Provide customer service. *Applicable legal basis under the GDPR: performance of a contract*;
- Monitor and analyze trends, usage and activities in connection with the App, and personalize and improve the App. *Applicable legal basis under the GDPR: legitimate interest*; and
- Detect, investigate and prevent fraudulent transactions and other illegal activities and protect the rights and property of Kinzoo and others. *Applicable legal basis under the GDPR: legitimate interest*.

We may use also information collected from parents in the App, or from individuals on our website or sources other than the App to:

- Obtain parental consent and to create your parent account. *Applicable legal basis under the GDPR: performance of a contract*;
- Administer your account and send you notifications, confirmations, updates, product announcements, security alerts, technical notices, and administrative messages. *Applicable legal basis under the GDPR: performance of a contract*;
- Provide customer service. *Applicable legal basis under the GDPR: performance of a contract*;
- Respond to your comments, questions, and requests, and otherwise facilitate or contact you about your or your children's use of the App. *Applicable legal basis under the GDPR: performance of a contract*;
- Notify you about important changes to the App and its terms and policies, *Applicable legal basis under the GDPR: consent*
- Monitor and analyze trends, usage and activities in connection with our website, products and services, and personalize and improve our website, products and services. *Applicable legal basis under the GDPR: legitimate interest*;
- Process and deliver contest entries and rewards. *Applicable legal basis under the GDPR: consent*; ******
- Communicate with you about marketing aspects such as products, services, offers, and events offered by Kinzoo and others, and provide news and information we think will be of interest to you. Note that in this circumstance, we will ask you to opt-in. *Applicable legal basis under the GDPR: consent*; ******
- Detect, investigate and prevent fraudulent transactions and other illegal activities and protect the rights and property of Kinzoo and others. *Applicable legal basis under the GDPR: legitimate interest*; and
- Carry out any other purpose for which consent is obtained, or as otherwise permitted or required by law. *Applicable legal basis under the GDPR: consent or legal obligation.*

# **Sharing of Information**

Information we collect may be shared as follows or as otherwise described in this Privacy Policy:

- With vendors, consultants and other service providers who need access to such information to carry out work on our behalf;
- In response to a request for information if we believe disclosure is in accordance with any applicable law, regulation or legal process, or as otherwise required by any applicable law, rule or regulation;
- If we believe an individual’s actions are inconsistent with the spirit or language of our user agreements or policies, or as we believe is appropriate to protect the rights, property and safety of Kinzoo, our users, or others;
- In connection with, or during negotiations of, any merger, sale of company assets, financing or acquisition, or in any other situation where personal information may be disclosed or transferred as one of the business assets of Kinzoo; We may share aggregated or anonymized information that does not constitute personal information, such as sharing analytics and other aggregate information about users’ activities that do not contain any personal information; and
- For any other purpose for which consent is obtained, or as otherwise permitted or required by law.

With respect to the App, information submitted by users through the App may be viewable by other users as follows:

- The first and last name and profile photograph of a child or adult user may be viewable by other users of the App; and
- The content of any communications sent by a user will be viewable by the user(s) to whom the communication is sent. For children, communications may only be sent to users who have been approved by parents as “Contacts” (in accordance with the parent approval and notification process for Contacts with children).

## Does the Kinzoo App enable children to make personal information publicly available?

The App enables children to share their first and last name and profile photograph with other users, and to share communications (which may include messages, photos, audio, video, and other personal information they choose to share in any communications) with other users who have been added as “Contacts” (in accordance with the parent approval and notification process). The foregoing information will also be available to parents of Contacts that are children.

# **Transfer of Information to the U.S. and Other Countries**

Kinzoo is based in Canada and we process and store information in Canada and the United States. As such, we and our service providers may transfer your information to, or store or access it in, jurisdictions that may not provide equivalent levels of data protection as your home jurisdiction. While such information is outside of your home jurisdiction, it is subject to the laws of the country in which it is located, and may be subject to disclosure to the governments, courts or law enforcement or regulatory agencies of such other country, pursuant to the laws of such country. However, our practices regarding your personal information will at all times continue to be governed by this Privacy Policy and, if applicable, we will notably comply with the GDPR requirements providing adequate protection for the transfer of personal information from the EU/EEA to third country, such as standard contractual clauses.

**Exercise your data subject rights under GDPR**

Kinzoo Technologies, Inc. is a company located outside of the European Union. In order to comply with Art 27 EU GDPR, GDPR-Rep.eu has been nominated as our representative in the European Union. If you want to make use of your data privacy rights, please visit: [Our public Privacy dashboard](https://gdpr-rep.eu/q/15974644).

# **Your Rights and Choices**

The GDPR provides rights to certain individuals located in the European Union. We apply these rights to all of our users.

Our Company would like to make sure you are fully aware of all of your data protection rights regarding the personal informaton we have about you or your children. More specifically, every user is entitled to the following:

- The right to access – You have the right to request Our Company for copies of your personal data. We may charge you a small fee for this service.
- The right to rectification – You have the right to request that Our Company correct any information you believe is inaccurate. You also have the right to request Our Company to complete the information you believe is incomplete.
- The right to erasure – You have the right to request that Our Company erase your personal data, under certain conditions.
- The right to restrict processing – You have the right to request that Our Company restrict the processing of your personal data, under certain conditions.
- The right to object to processing – You have the right to object to Our Company’s processing of your personal data, under certain conditions.
- The right to data portability – You have the right to request that Our Company transfer the data that we have collected to another organization, or directly to you, under certain conditions.
- The right not to be subject to decisions being taken by automated means which produce legal effects concerning you or similarly significantly affect you - You have the right to object to an automated decision making under certain conditions;
- The right to lodge a complaint, or if you feel that Our Company has not addressed your concern in a satisfactory manner, with your local data protection authority.

If you want to learn more about your rights under the GDPR, you can visit the European Commission’s page on Data Protection at: http://ec.europa.eu/justice/data-protection/index_en.htm

If you make a request, we have one month to respond to you, it being specified that this period may be extended by two further months where necessary, taking into account the complexity and number of the requests.

We may require you to provide with sufficient identification to fulfill your request.

If you would like to exercise any of these rights or to contact us about these rights, please contact us at privacy@kinzoo.com.

## Promotional Communications

You may opt out of receiving promotional emails from Kinzoo by following the unsubscribe instructions in those emails or by using the contact information in the “Contact Us” section below. If you opt out, we may still send you non-promotional emails, such as those about your account or our ongoing business relations.

## Account Information

Parents may update or correct information about them at any time by logging into their parent account. Parents may also delete their accounts and their child’s accounts) by going to their Settings and choosing the option to delete the account, but note that we may retain certain information as required by law or for legitimate business purposes.

## Mobile Push Notifications/Alerts

With your consent, we may send push notifications or alerts relating to activities on the App to your or your child’s mobile device. You can deactivate these messages at any time by changing the notification settings on your or your child’s mobile device.

# **Information Retention**

We will use, disclose or retain your personal information only for as long as necessary to fulfill the purposes for which that personal information was collected and as permitted or required by law.

# **Information Security**

We have implemented physical, organizational, contractual and technological security measures to protect your personal information. If we provide your information to service providers, we require that the service providers maintain the confidentiality of your personal information and keep your personal information secure. We also require that they only use your personal information for the limited purposes for which it is provided. In some circumstances, we may permit our service providers to retain aggregated, anonymized or statistical information that does not identify you.

# **Parent Account**

Your Parent Account will be associated with each of your Child Profiles in Kinzoo, and you agree to be contacted, notably by email, in the following situations regarding activity in your child’s Profile:

- If your child has reported another user in Kinzoo for inappropriate content or conduct;
- If your child has been reported by another user in Kinzoo for inappropriate content or conduct that is deemed to violate Kinzoo's [Terms of Service](https://kinzoo.com/terms/) and/or [Community Guidelines](https://kinzoo.com/community-guidelines/);
- If your child has used Kinzoo’s in-app feedback form to request support or assistance using Kinzoo and its related features.

# **Contact Us**

Any comments, questions, concerns or complaints regarding your personal information or our privacy practices may be sent to our Privacy Officer as follows:

Attention: Sean Herman, Privacy Officer  
Kinzoo Technologies, Inc.  
1066 – 999 W Hastings St  
Vancouver, BC V6C 2W2  
Tel: (604) 868-9719  
privacy@kinzoo.com

**For Data Subjects in the EU:**

We value your data subject rights under GDPR and therefore appointed GDPR-Rep.eu as representative according to Art 27 GDPR and provide you with an easy way to submit us privacy related request like a requests to access or erase your personal data. If you want to

make use of your data subject rights, please visit: https://gdpr-rep.eu/q/15974644

Contact GDPR-Rep.e  
GDPR-Rep.e  
Maetzler Rechtsanwalts GmbH & Co KG  
Attorneys at Law  
c/o Kinzoo Technologies, Inc.  
Schellinggasse 3/10, 1010 Vienna, Austria
`

function PrivacyPolicy({data}) {
  const {
    referencePrivacyPolicyMetaData,
  } = data.contentfulSingletonStaticPageMetadata

  return (
    <PageContainer>
      <Helmet>
        <meta charSet="utf-8" />
        <link rel="canonical" href="https://kinzoo.com/privacy-policy/" />
        <meta name="og:url" content="https://kinzoo.com/privacy-policy/" />=
      </Helmet>
      <Metadata {...referencePrivacyPolicyMetaData} />
      <Header />
      

      <article className="generic-page">
        <div className="generic-page__head">
          <div className="grid generic-page__grid">
            <h1 className="generic-page__hed column-start-1 column-end-6 column-start-2--t column-end-11--t column-start-3--d column-end-10--d">
              Kinzoo Privacy Policy
            </h1>
            <p className="generic-page__dek column-start-1 column-end-6 column-start-2--t column-end-11--t column-start-3--d column-end-10--d">
              Last Revised: August 26, 2020
            </p>
          </div>
        </div>
        <div className="grid">
          <div className="blog-article__article column-start-1 column-start-2--t column-start-3--d column-end-6 column-end-11--t column-end-10--d">
            {compile(privacyPolicy).tree}
            <br />
            <br />
            <br />
            <br />
          </div>
        </div>
      </article>
      <Footer />
    </PageContainer>
  )
}

export default PrivacyPolicy

export const pageQuery = graphql`
  query PrivacyPolicyQuery {
    contentfulSingletonStaticPageMetadata(contentful_id: {eq: "37wd06meITbnwAFYKdxp70"}) {
      referencePrivacyPolicyMetaData {
        description
        keywords
        ogDescription
        ogTitle
        ogType
        pageTitle
        twitterCard
        twitterCreator
        twitterDescription
        twitterTitle
        ogImage {
          file {
            url
          }
        }
        twitterImage {
          file {
            url
          }
        }
      }
    }
  }
`
